import { IDENTITIES_KEYS, IDENTITY_STATE, } from '../types';
import { getCache } from './cache';
import { CACHE_MAX_AGE, EVENT_NAME, EVENT_NAME_BEFORE_UPDATE, TIMEOUT } from './consts';
import { decodeString, encodeString } from './encoding';
import { prematureSettling, pullExternalsAsync, pullExternalsSync } from './externals';
import { cacheAndNotify, getValueLevel, prepareIdentityStorageObject, sanitizeIdentities } from './general';
import { debug, dotLog } from './debug';
import { initCrossFramesCommunication } from './frames';
import { isEqual } from './utils';
import { identityOutput, isOK, validateIdentificator } from './identities';
import { rulesDefaults, rulesDefaultsAll } from './getrules';
import { isAvailable, getGlobalConfig } from './config';
import { getIS } from './namespace';
import { sendEvent } from './events';
/**
 * Decode and parse storage.
 * It decoded global object or can be used for "on demand" decoding.
 */
const _decodeStorage = (toDecode = '') => {
    var _a, _b;
    const decoding = toDecode || ((_b = (_a = getIS()) === null || _a === void 0 ? void 0 : _a._storage) === null || _b === void 0 ? void 0 : _b.identities);
    if (!decoding) {
        return {};
    }
    const data = decodeString(decoding);
    try {
        return data ? JSON.parse(data) : {};
    }
    catch (err) {
        debug({ type: 'warn', message: 'Storage could not be decoded, setting empty object', err });
        return {};
    }
};
/**
 * Encode and serialize storage.
 */
const _encodeStorage = (identities, dryRun = false) => {
    const encoded = encodeString(JSON.stringify(identities));
    if (dryRun) {
        return encoded;
    }
    return (getIS()._storage.identities = encoded);
};
/**
 * Init global storage.
 * Prefill with cached data if exist and valid.
 */
export const initStorage = (force = false, mergeToInit = {}) => {
    var _a, _b;
    // already inited?
    if (!force && ((_b = (_a = getIS()) === null || _a === void 0 ? void 0 : _a._storage) === null || _b === void 0 ? void 0 : _b.inited)) {
        return;
    }
    debug('Storage initializing');
    const cache = getCache();
    let initObject = {};
    let cacheDecoded = {};
    // is cache usable?
    try {
        cacheDecoded = _decodeStorage(cache);
        const cacheKeys = Object.keys(cacheDecoded);
        // filter invalid keys from cache
        const cacheValidKeys = cacheKeys.filter((k) => k in IDENTITIES_KEYS);
        if (cacheValidKeys.length) {
            initObject = cacheValidKeys.reduce((acc, key) => {
                // check cache age + set state to "cache"
                const cacheObject = cacheDecoded[key];
                if (validateIdentificator(key) &&
                    isAvailable(key) &&
                    cacheObject.updated >= Date.now() - CACHE_MAX_AGE) {
                    acc[key] = Object.assign(Object.assign({}, cacheObject), { state: IDENTITY_STATE.cache, settled: false });
                }
                return acc;
            }, {});
        }
    }
    catch (err) {
        debug({ type: 'error', message: 'Storage cache could not be decoded / inited', err });
    }
    // INIT object priority > cache priority
    initObject = Object.assign(Object.assign({}, initObject), mergeToInit);
    const getter = () => getStorage(rulesDefaultsAll({ rich: true }));
    // externals sync
    pullExternalsSync(initObject);
    debug({ message: 'Storage initialized', initObject });
    prepareIdentityStorageObject({
        _childFramesToFeed: [],
        _storage: {
            identities: Object.keys(initObject).length ? _encodeStorage(initObject, true) || null : null,
            inited: true,
        },
    });
    // externals async
    pullExternalsAsync(initObject, updateStorage, getter);
    // cache and let know rest of the world
    cacheAndNotify(getIS()._storage.identities, initObject, Object.keys(initObject));
    // init cross frames cooperation
    initCrossFramesCommunication(updateStorage, getter);
};
/**
 * Get whole storage (decoded).
 */
export const getStorage = (rules) => {
    try {
        const storage = _decodeStorage();
        const storageHandled = Object.keys(storage).reduce((acc, key) => {
            const k = key;
            const { rich, justSettled, cacheEnough } = rules[k];
            const identity = storage[k];
            if (validateIdentificator(k) && isAvailable(k) && isOK(identity, cacheEnough, justSettled)) {
                acc[k] = identityOutput(identity, rich);
            }
            return acc;
        }, {});
        return storageHandled || {};
    }
    catch (err) {
        debug({ type: 'warn', message: 'Can not read storage, returning {}', err });
        return {};
    }
};
/**
 * Get single item from storage.
 * Validates identificator.
 */
export const getStorageItem = (identificator, rules) => {
    const mainIdentificatorPart = identificator.split('.')[0];
    if (!isAvailable(mainIdentificatorPart)) {
        return null;
    }
    const { rich, justSettled, cacheEnough } = rulesDefaults(rules);
    if (!validateIdentificator(identificator)) {
        debug({ type: 'warn', message: `Identity identificator (${identificator}) not valid, returning NULL` });
        return null;
    }
    try {
        const valueObject = _decodeStorage()[mainIdentificatorPart];
        return valueObject && isOK(valueObject, cacheEnough, justSettled)
            ? getValueLevel(identificator, identityOutput(valueObject, rich))
            : null;
    }
    catch (err) {
        debug({ type: 'warn', message: 'Identity could not be retrived, returning NULL', err });
        return null;
    }
};
/**
 * Get single item from storage as promise (get current value / listen to events / timeout)
 * Validates identificator.
 */
export const awaitStorageItem = (identificator, rules, doNotTimeout = false, satisfiedBy, logTimeouts) => {
    if (!isAvailable(identificator)) {
        return Promise.resolve(null);
    }
    // log timeouts to reporter
    const log = (type) => logTimeouts && dotLog({ data: { action: 'IS-awaitStorageItem-timeout', identificator, type } });
    const { rich, cacheEnough } = rulesDefaults(rules);
    return new Promise((resolve) => {
        var _a, _b;
        const identity = getStorageItem(identificator, { rich: true, cacheEnough });
        if (identity && isOK(identity, cacheEnough) && (!satisfiedBy || satisfiedBy(identity))) {
            // we have fresh data already in storage
            resolve(identityOutput(identity, rich));
        }
        else {
            // timeouts for settled identity
            let timeout = null;
            let prematureTimeout = null;
            const clearTimeouts = () => {
                prematureTimeout && window.clearTimeout(prematureTimeout);
                timeout && window.clearTimeout(timeout);
            };
            // total timeout
            if (!doNotTimeout) {
                timeout = window.setTimeout(() => {
                    const identity = getStorageItem(identificator, { rich: true, cacheEnough });
                    clearTimeouts();
                    log('normal');
                    resolve(identity ? identityOutput(identity, rich) : null);
                }, ((_b = (_a = getGlobalConfig()) === null || _a === void 0 ? void 0 : _a.timeouts) === null || _b === void 0 ? void 0 : _b[identificator]) || TIMEOUT);
            }
            // small timeout - things do not look like waiting for full settled identity is worth the time
            if (!doNotTimeout && prematureSettling[identificator]) {
                prematureTimeout = prematureSettling[identificator]((prematureIdentity) => {
                    clearTimeouts();
                    log('premature');
                    resolve(prematureIdentity);
                });
            }
            // we are waiting for events (till timeout)
            const handler = (event) => {
                var _a, _b, _c;
                const data = event.detail;
                if (~((_a = data === null || data === void 0 ? void 0 : data.changes) === null || _a === void 0 ? void 0 : _a.indexOf(identificator)) &&
                    isOK((_b = data === null || data === void 0 ? void 0 : data.identities) === null || _b === void 0 ? void 0 : _b[identificator], cacheEnough) &&
                    (!satisfiedBy || satisfiedBy((_c = data === null || data === void 0 ? void 0 : data.identities) === null || _c === void 0 ? void 0 : _c[identificator]))) {
                    window.removeEventListener(EVENT_NAME, handler);
                    clearTimeouts();
                    resolve(identityOutput(event.detail.identities[identificator], rich));
                }
            };
            window.addEventListener(EVENT_NAME, handler);
        }
    });
};
/**
 * Purge storage object. Optionally specify just some identitites for purging.
 */
export const clearStorage = (identities = Object.values(IDENTITIES_KEYS)) => {
    const storage = _decodeStorage();
    if (Object.keys(storage).length) {
        const newStorage = identities.reduce((acc, IDENTITY_KEY) => {
            if (acc.storage[IDENTITY_KEY] !== undefined) {
                delete acc.storage[IDENTITY_KEY];
                acc.changes.push(IDENTITY_KEY);
            }
            return acc;
        }, { storage: Object.assign({}, storage), changes: [] });
        if (!newStorage.changes.length) {
            return;
        }
        cacheAndNotify(_encodeStorage(newStorage.storage), newStorage.storage, newStorage.changes);
    }
};
/**
 * Update (merge) current storage with submited data.
 * Validates submited object and ads metadata (timestamps, ...).
 */
export const updateStorage = (identities) => {
    debug({ message: 'Updating storage', identities });
    if (!identities) {
        debug({ type: 'error', message: 'Invalid identities object', identities });
        return;
    }
    const storage = _decodeStorage();
    const sanitizedIdentities = sanitizeIdentities(storage, identities);
    const newKeys = Object.keys(sanitizedIdentities);
    if (newKeys.length) {
        // find changed keys (old vs new storage state)
        const notEqualKeys = newKeys.filter((sKey) => {
            const newIdentity = (sanitizedIdentities[sKey] || {});
            const currentIdentity = (storage[sKey] || {});
            // do not fire unnecessary events when clearing non existing identity
            if (currentIdentity.value === undefined && newIdentity.value === null) {
                return false;
            }
            return (!isEqual(newIdentity.value, currentIdentity.value) ||
                newIdentity.state !== currentIdentity.state ||
                newIdentity.settled !== currentIdentity.settled);
        });
        // update and notify only if old != new (at least one key changed)
        if (notEqualKeys.length) {
            const newStorage = Object.assign(Object.assign({}, (storage || {})), sanitizedIdentities);
            // delete identitites with NULL value (for example LOGIN change can trigger CONSENT removal)
            const newStorageCleaned = Object.keys(newStorage).reduce((acc, key) => {
                if (newStorage[key].value !== null) {
                    acc[key] = newStorage[key];
                }
                else {
                    debug({ message: 'Removing identity with NULL value', key });
                }
                return acc;
            }, {});
            debug({ message: 'Updating storage - after cleanup', newStorageCleaned });
            sendEvent(EVENT_NAME_BEFORE_UPDATE, {
                oldIdentities: storage,
                newIdentities: newStorageCleaned,
                changes: notEqualKeys,
            });
            cacheAndNotify(_encodeStorage(newStorageCleaned), newStorageCleaned, notEqualKeys);
        }
    }
    else {
        debug({ message: `No identities to update after sanitizing / validation`, identities });
    }
};
